import { VariableType } from 'json-to-graphql-query';
import { AssetNodeType, AssetNodeTypeNoPerms } from 'src/dynamoQueries/assets';
import type { FieldValue, PageInfoFields } from './types';

interface OwnerNodeFields {
  user: {
    username: FieldValue;
  };
  firstName: FieldValue;
  lastName: FieldValue;
  id: FieldValue;
}

interface MediaGroupNodeFields {
  id: FieldValue;
  name: FieldValue;
}

interface CollectionNodeTypeResult {
  __typeName: 'CollectionNodeType';
  id: FieldValue;
  collectionType: FieldValue;
  name: FieldValue;
  dateAdded: FieldValue;
  owner: OwnerNodeFields;
  items: {
    totalCount: FieldValue;
  };
  canShare: FieldValue;
  mediaGroup: MediaGroupNodeFields;
}

interface ShareCollectionQueryResult {
  query: {
    __variables: {
      id: 'ID!';
    };
    sharedCollection: {
      __args: {
        id: VariableType;
      };
      id: FieldValue;
      collectionType: FieldValue;
    };
  };
}

interface CollectionSimpleQueryResult {
  query: {
    __variables: {
      id: 'ID!';
      before: 'String';
      after: 'String';
      first: 'Int';
      last: 'Int';
    };
    collection?: {
      __args: {
        id: VariableType;
      };
      id: FieldValue;
      name: FieldValue;
      owner: OwnerNodeFields;
      dateAdded: FieldValue;
      items: {
        __args: {
          before: VariableType;
          after: VariableType;
          first: VariableType;
          last: VariableType;
        };
        totalCount: FieldValue;
        pageInfo: PageInfoFields;
        edges: {
          cursor: FieldValue;
          node: {
            id: FieldValue;
            contentObject: {
              __on: {
                __typeName: 'AssetNodeType';
                id: FieldValue;
              };
            };
          };
        };
      };
    };
    sharedCollection?: {
      __args: {
        id: VariableType;
      };
      id: FieldValue;
      name: FieldValue;
      owner: OwnerNodeFields;
      dateAdded: FieldValue;
      items: {
        __args: {
          before: VariableType;
          after: VariableType;
          first: VariableType;
          last: VariableType;
        };
        totalCount: FieldValue;
        pageInfo: PageInfoFields;
        edges: {
          cursor: FieldValue;
          node: {
            id: FieldValue;
            contentObject: {
              __on: {
                __typeName: 'AssetNodeType';
                id: FieldValue;
              };
            };
          };
        };
      };
    };
  };
}

interface CollectionQueryResult {
  query: {
    __variables: {
      id: 'ID!';
    };
    collection: CollectionNodeTypeResult & {
      __args: {
        id: VariableType;
      };
    };
  };
}

interface AllCollectionsQueryResult {
  query: {
    __variables: {
      id: 'ID';
      name: 'String';
      collectionType: 'LibraryCollectionCollectionTypeChoices';
      hasPermission: 'String';
    };
    allCollections: {
      __args: {
        id: VariableType;
        name: VariableType;
        collectionType: VariableType;
        hasPermission: VariableType;
      };
      edges: {
        node: CollectionNodeTypeResult;
      };
    };
  };
}

interface CollectionItemsResult {
  __args: {
    before: VariableType;
    after: VariableType;
    first: VariableType;
    last: VariableType;
    sequence: VariableType;
    orderBy: VariableType;
    customOrder: VariableType;
    collection_Name: VariableType;
  };
  pageInfo: PageInfoFields;
  totalCount: FieldValue;
  edges: {
    node: {
      id: FieldValue;
      sequence: FieldValue;
      mediaGroupId: FieldValue;
      objectId: FieldValue;
      contentObject: {
        __on: ReturnType<typeof AssetNodeType>;
      };
    };
    cursor: FieldValue;
  };
}

interface CollectionItemsNoPermsResult {
  __args: {
    before: VariableType;
    after: VariableType;
    first: VariableType;
    last: VariableType;
    sequence: VariableType;
    orderBy: VariableType;
    customOrder: VariableType;
    collection_Name: VariableType;
  };
  pageInfo: PageInfoFields;
  totalCount: FieldValue;
  edges: {
    node: {
      id: FieldValue;
      sequence: FieldValue;
      mediaGroupId: FieldValue;
      objectId: FieldValue;
      contentObject: {
        __on: ReturnType<typeof AssetNodeTypeNoPerms>;
      };
    };
    cursor: FieldValue;
  };
}

interface CollectionMutationResult {
  id: FieldValue;
  name: FieldValue;
  owner: {
    firstName: FieldValue;
  };
  mediaGroup: MediaGroupNodeFields;
}

interface CreateGalleryFromExistingMutationResult {
  mutation: {
    __variables: {
      collectionId: 'ID!';
      mediaGroupId: 'ID!';
      name: 'String';
    };
    createGalleryFromExistingCollection: {
      __args: {
        collectionId: VariableType;
        mediaGroupId: VariableType;
        name: VariableType;
      };
      collection: CollectionMutationResult;
    };
  };
}

interface CreateGalleryMutationResult {
  mutation: {
    __variables: {
      assetIds: '[ID]';
      mediaGroupId: 'ID!';
      name: 'String!';
    };
    createCollection: {
      __args: {
        assetIds: VariableType;
        mediaGroupId: VariableType;
        name: VariableType;
        collectionType: 'GALLERY';
      };
      collection: CollectionMutationResult;
    };
  };
}

interface UpdateGalleryMutationResult {
  mutation: {
    __variables: {
      collectionId: 'ID!';
      assetIds: '[ID]';
    };
    updateCollection: {
      __args: {
        assetIds: VariableType;
        collectionId: VariableType;
      };
      collection: CollectionMutationResult;
    };
  };
}

interface UpdateCollectionMutationResult {
  mutation: {
    __variables: {
      collectionId: 'ID!';
      name: 'String';
      mediaGroupId: 'ID';
    };
    updateCollection: {
      __args: {
        collectionId: VariableType;
        name: VariableType;
        mediaGroupId: VariableType;
      };
      collection: CollectionMutationResult;
    };
  };
}

interface RemoveItemFromCollectionMutationResult {
  mutation: {
    __variables: {
      collectionId: 'ID!';
      assetIds: '[ID]';
    };
    deleteItemsFromCollection: {
      __args: {
        collectionId: VariableType;
        assetIds: VariableType;
      };
      collection: {
        id: FieldValue;
      };
    };
  };
}

export const CollectionNodeType = (truify: boolean = false): CollectionNodeTypeResult => ({
  __typeName: 'CollectionNodeType',
  id: truify,
  collectionType: true,
  name: true,
  dateAdded: truify,
  owner: {
    user: {
      username: truify,
    },
    firstName: truify,
    lastName: truify,
    id: truify,
  },
  items: {
    totalCount: truify,
  },
  canShare: false,
  mediaGroup: {
    id: truify,
    name: truify,
  },
});

export const ShareCollection = (truify: boolean = false): ShareCollectionQueryResult => ({
  query: {
    __variables: {
      id: 'ID!',
    },
    sharedCollection: {
      __args: {
        id: new VariableType('id'),
      },
      id: true,
      collectionType: truify,
    },
  },
});

export const CollectionSimple = (truify: boolean = false, shared: boolean = false): CollectionSimpleQueryResult => ({
  query: {
    __variables: {
      id: 'ID!',
      before: 'String',
      after: 'String',
      first: 'Int',
      last: 'Int',
    },
    [shared ? 'sharedCollection' : 'collection']: {
      __args: {
        id: new VariableType('id'),
      },
      id: true,
      name: true,
      owner: {
        user: { username: truify },
        firstName: truify,
        lastName: truify,
        id: truify,
      },
      dateAdded: truify,
      items: {
        __args: {
          before: new VariableType('before'),
          after: new VariableType('after'),
          first: new VariableType('first'),
          last: new VariableType('last'),
        },
        totalCount: true,
        pageInfo: {
          endCursor: true,
          startCursor: true,
          hasNextPage: true,
          hasPreviousPage: true,
        },
        edges: {
          cursor: true,
          node: {
            id: truify,
            contentObject: {
              __on: {
                __typeName: 'AssetNodeType',
                id: true,
              },
            },
          },
        },
      },
    },
  },
});

export const Collection = (truify: boolean = false): CollectionQueryResult => ({
  query: {
    __variables: {
      id: 'ID!',
    },
    collection: {
      __args: {
        id: new VariableType('id'),
      },
      ...CollectionNodeType(truify),
    },
  },
});

export const allCollections = (truify: boolean = false): AllCollectionsQueryResult => ({
  query: {
    __variables: {
      id: 'ID',
      name: 'String',
      collectionType: 'LibraryCollectionCollectionTypeChoices',
      hasPermission: 'String',
    },
    allCollections: {
      __args: {
        id: new VariableType('id'),
        name: new VariableType('name'),
        collectionType: new VariableType('collectionType'),
        hasPermission: new VariableType('hasPermission'),
      },
      edges: {
        node: {
          ...CollectionNodeType(truify),
        },
      },
    },
  },
});

export const collectionItems = (truify: boolean = false): CollectionItemsResult => ({
  __args: {
    before: new VariableType('before'),
    after: new VariableType('after'),
    first: new VariableType('first'),
    last: new VariableType('last'),
    sequence: new VariableType('sequence'),
    orderBy: new VariableType('orderBy'),
    customOrder: new VariableType('customOrder'),
    collection_Name: new VariableType('collection_Name'),
  },
  pageInfo: {
    hasNextPage: truify,
    hasPreviousPage: truify,
    startCursor: truify,
    endCursor: truify,
  },
  totalCount: truify,
  edges: {
    node: {
      id: true,
      sequence: true,
      mediaGroupId: true,
      objectId: true,
      contentObject: {
        __on: AssetNodeType(true),
      },
    },
    cursor: true,
  },
});

export const collectionItemsNoPerms = (truify: boolean = false): CollectionItemsNoPermsResult => ({
  __args: {
    before: new VariableType('before'),
    after: new VariableType('after'),
    first: new VariableType('first'),
    last: new VariableType('last'),
    sequence: new VariableType('sequence'),
    orderBy: new VariableType('orderBy'),
    customOrder: new VariableType('customOrder'),
    collection_Name: new VariableType('collection_Name'),
  },
  pageInfo: {
    hasNextPage: truify,
    hasPreviousPage: truify,
    startCursor: truify,
    endCursor: truify,
  },
  totalCount: truify,
  edges: {
    node: {
      id: true,
      sequence: true,
      mediaGroupId: true,
      objectId: true,
      contentObject: {
        __on: AssetNodeTypeNoPerms(true),
      },
    },
    cursor: true,
  },
});

export const createGalleryFromExistingCollection = (
  truify: boolean = false,
): CreateGalleryFromExistingMutationResult => ({
  mutation: {
    __variables: {
      collectionId: 'ID!',
      mediaGroupId: 'ID!',
      name: 'String',
    },
    createGalleryFromExistingCollection: {
      __args: {
        collectionId: new VariableType('collectionId'),
        mediaGroupId: new VariableType('mediaGroupId'),
        name: new VariableType('name'),
      },
      collection: {
        id: truify,
        name: truify,
        owner: {
          firstName: truify,
        },
        mediaGroup: {
          name: truify,
          id: truify,
        },
      },
    },
  },
});

export const createGallery = (truify: boolean = false): CreateGalleryMutationResult => ({
  mutation: {
    __variables: {
      assetIds: '[ID]',
      mediaGroupId: 'ID!',
      name: 'String!',
    },
    createCollection: {
      __args: {
        assetIds: new VariableType('assetIds'),
        mediaGroupId: new VariableType('mediaGroupId'),
        name: new VariableType('name'),
        collectionType: 'GALLERY',
      },
      collection: {
        id: truify,
        name: truify,
        owner: {
          firstName: truify,
        },
        mediaGroup: {
          name: truify,
          id: truify,
        },
      },
    },
  },
});

export const updateGallery = (truify: boolean = false): UpdateGalleryMutationResult => ({
  mutation: {
    __variables: {
      collectionId: 'ID!',
      assetIds: '[ID]',
    },
    updateCollection: {
      __args: {
        assetIds: new VariableType('assetIds'),
        collectionId: new VariableType('collectionId'),
      },
      collection: {
        id: truify,
        name: truify,
        owner: {
          firstName: truify,
        },
        mediaGroup: {
          name: truify,
          id: truify,
        },
      },
    },
  },
});

export const updateCollection = (truify: boolean = false): UpdateCollectionMutationResult => ({
  mutation: {
    __variables: {
      collectionId: 'ID!',
      name: 'String',
      mediaGroupId: 'ID',
    },
    updateCollection: {
      __args: {
        collectionId: new VariableType('collectionId'),
        name: new VariableType('name'),
        mediaGroupId: new VariableType('mediaGroupId'),
      },
      collection: {
        id: truify,
        name: truify,
        owner: {
          firstName: truify,
        },
        mediaGroup: {
          name: truify,
          id: truify,
        },
      },
    },
  },
});

export const removeItemFromCollection = (truify: boolean = false): RemoveItemFromCollectionMutationResult => ({
  mutation: {
    __variables: {
      collectionId: 'ID!',
      assetIds: '[ID]',
    },
    deleteItemsFromCollection: {
      __args: {
        collectionId: new VariableType('collectionId'),
        assetIds: new VariableType('assetIds'),
      },
      collection: {
        id: truify,
      },
    },
  },
});
